import { CODE_SYSTEMS, LOINC_CODES } from '@liza/fhir-utils';
import type { PlanDefinition, PlanDefinitionAction, Timing } from '@medplum/fhirtypes';
import { delivery, every, itemKind, loincCode, patient, taskCategory } from './builders';

export const COPD_ONGOING_MEASUREMENTS_URL = 'https://liza.health/fhir/PlanDefinition/copd-ongoing-measurements';
export const COPD_ONGOING_MEASUREMENTS_VERSION = '1.0.0';
export const ASTHMA_ONGOING_MEASUREMENTS_URL = 'https://liza.health/fhir/PlanDefinition/asthma-ongoing-measurements';
export const ASTHMA_ONGOING_MEASUREMENTS_VERSION = '1.0.0';
/** Version-pinned canonicals, the values an expanded suggestion records as its provenance. */
export const COPD_ONGOING_MEASUREMENTS_CANONICAL = `${COPD_ONGOING_MEASUREMENTS_URL}|${COPD_ONGOING_MEASUREMENTS_VERSION}`;
export const ASTHMA_ONGOING_MEASUREMENTS_CANONICAL = `${ASTHMA_ONGOING_MEASUREMENTS_URL}|${ASTHMA_ONGOING_MEASUREMENTS_VERSION}`;

/**
 * GOLD states its monitoring cadences in the follow-up prose of the pocket guide rather than in
 * numbered recommendations, so a citation names the section it sits in.
 */
const GOLD_2024 = {
  title: 'GOLD Global Strategy for Prevention, Diagnosis and Management of COPD (2024)',
  url: 'https://goldcopd.org/2024-gold-report/',
} as const;

/** GINA likewise puts every monitoring statement in running prose, not in a numbered Box. */
const GINA_2023 = {
  title: 'GINA Global Strategy for Asthma Management and Prevention (2023)',
  url: 'https://ginasthma.org/reports/',
} as const;

/** The LOINC code both protocols track, named from the shared `LOINC_CODES`. */
export const RESPIRATORY_ONGOING_CODES = {
  FEV1: LOINC_CODES.FEV1,
  OXYGEN_SATURATION: LOINC_CODES.OXYGEN_SATURATION,
  PEAK_EXPIRATORY_FLOW: LOINC_CODES.PEAK_EXPIRATORY_FLOW,
} as const;

/**
 * `J44` is an ICD-10 *prefix* covering `J44.0`, `J44.1` and `J44.9`; the SNOMED code is matched
 * exactly. Both systems are listed because a diagnosis reaches the chart in whichever system its
 * source used (#10129).
 */
export const COPD_FOCUS_CODINGS = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'J44', display: 'Chronic obstructive pulmonary disease' },
  { system: CODE_SYSTEMS.SNOMED, code: '13645005', display: 'Chronic obstructive pulmonary disease' },
] as const;

/** `J45` is an ICD-10 prefix covering the asthma category, including its severity subdivisions. */
export const ASTHMA_FOCUS_CODINGS = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'J45', display: 'Asthma' },
  { system: CODE_SYSTEMS.SNOMED, code: '195967001', display: 'Asthma' },
] as const;

/**
 * Spirometry is a measurement, in the same sense eGFR is: the patient does not produce the value —
 * a trained operator runs the device, as a phlebotomist and an analyser produce an eGFR — but the
 * result is the patient's, belongs on their chart, and is theirs to see. `participant: patient` marks
 * whose result it is, not who performs it.
 *
 * The one thing it does not capture is that this needs a booked visit, where a lab draw usually does
 * not. If that distinction should show on the plan it is a modelling question for every clinic-
 * performed result, not for these two protocols.
 */
const measurement = (
  id: string,
  title: string,
  description: string,
  code: string,
  /** Omitted where the guideline asks for the result without naming an interval. */
  timing: Timing | undefined,
  locus: string,
  source: typeof GOLD_2024 | typeof GINA_2023,
): PlanDefinitionAction => ({
  id,
  title,
  description,
  code: loincCode(code),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  ...(timing ? { timingTiming: timing } : {}),
  documentation: [{ type: 'citation', display: `${source.title}, ${locus}`, url: source.url }],
});

// --- actions ------------------------------------------------------------------------------------------

/**
 * GOLD asks for spirometry at least annually, unconditionally. The issue body pairs that with "or
 * with symptom change", which GOLD does not say — what it scales by severity is the *review visit*,
 * not the spirometry interval.
 */
const copdSpirometry = measurement(
  'spirometry',
  'Spirometry',
  'Spirometry at least yearly, to track FEV1.',
  RESPIRATORY_ONGOING_CODES.FEV1,
  every(1, 'a'),
  'follow-up prose',
  GOLD_2024,
);

/**
 * No cadence: GOLD asks for oximetry on clinical signs of respiratory or right heart failure, and
 * once 60-90 days after starting long-term oxygen — situational triggers, not an interval. As-needed
 * puts the result on the plan without asserting a schedule the guideline does not give.
 */
const copdOximetry = measurement(
  'oxygen-saturation',
  'Oxygen saturation',
  'Oxygen saturation, as needed, on signs of respiratory or right heart failure.',
  RESPIRATORY_ONGOING_CODES.OXYGEN_SATURATION,
  undefined,
  'assessment prose',
  GOLD_2024,
);

/**
 * GINA gives a two-year floor for most patients, raising it for anyone at high exacerbation risk or
 * with risk factors for lung-function decline — a gate this protocol cannot evaluate, so the floor is
 * what fires. Two one-off assessments precede it (at diagnosis, and once at 3-6 months to establish a
 * personal best); neither is a recurring cadence.
 */
const asthmaSpirometry = measurement(
  'spirometry',
  'Lung function',
  'Lung function at least every two years.',
  RESPIRATORY_ONGOING_CODES.FEV1,
  every(2, 'a'),
  'Chapter 2 prose',
  GINA_2023,
);

/**
 * No cadence: GINA recommends routine peak-flow monitoring for severe asthma or for patients with
 * impaired perception of airflow limitation — a gate this protocol cannot evaluate. As-needed keeps
 * the result available without scheduling it for everyone with the diagnosis.
 */
const asthmaPeakFlow = measurement(
  'peak-flow',
  'Peak flow',
  'Peak expiratory flow, as needed.',
  RESPIRATORY_ONGOING_CODES.PEAK_EXPIRATORY_FLOW,
  undefined,
  'Chapter 2 prose',
  GINA_2023,
);

/** No cadence: oximetry belongs to exacerbation care, where GINA gives a target range, not an interval. */
const asthmaOximetry = measurement(
  'oxygen-saturation',
  'Oxygen saturation',
  'Oxygen saturation, as needed, during an exacerbation.',
  RESPIRATORY_ONGOING_CODES.OXYGEN_SATURATION,
  undefined,
  'Chapter 4',
  GINA_2023,
);

// --- the protocols ------------------------------------------------------------------------------------

const clinicalProtocolType = {
  coding: [
    {
      system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
      code: 'clinical-protocol',
      display: 'Clinical Protocol',
    },
  ],
};

/**
 * What GOLD asks for on an ongoing basis in COPD.
 *
 * Only spirometry has a cadence to encode. Pulse oximetry is situational — GOLD asks for it on
 * clinical signs of respiratory or right heart failure, plus a one-off recheck 60-90 days after
 * starting long-term oxygen therapy — and the symptom and exacerbation review is deliberately given
 * as "a suitable interval", shorter in severe disease and longer in mild, with no number attached.
 * Synthesising one would assert a cadence the guideline declines to give.
 */
export const copdOngoingMeasurements: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: COPD_ONGOING_MEASUREMENTS_URL,
  version: COPD_ONGOING_MEASUREMENTS_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: COPD_ONGOING_MEASUREMENTS_URL }],
  name: 'copd-ongoing-measurements',
  title: 'COPD — ongoing measurements',
  extension: delivery('suggest'),
  status: 'active',
  experimental: true,
  type: clinicalProtocolType,
  date: '2026-09-15',
  publisher: 'Liza Health',
  description:
    'Results recommended for ongoing monitoring in COPD, expanded onto a care-plan topic when a COPD Condition is linked to it.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...COPD_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [{ type: 'derived-from', display: GOLD_2024.title, url: GOLD_2024.url }],
  action: [
    {
      id: 'ongoing-measurements',
      title: 'Ongoing measurements',
      description: 'Results tracked on an ongoing basis for COPD.',
      action: [copdSpirometry, copdOximetry],
    },
  ],
};

/**
 * What GINA asks for on an ongoing basis in asthma.
 *
 * Peak flow is on the plan as-needed rather than on a cadence: GINA recommends routine monitoring
 * only for severe asthma or for patients with impaired perception of airflow limitation, a gate this
 * protocol cannot evaluate, so scheduling it for everyone with the diagnosis would over-monitor.
 * Oxygen saturation is as-needed for the reason in the other direction — it belongs to acute
 * exacerbation care, where GINA gives a target range rather than an interval.
 *
 * The symptom and control review has a real interval (1-3 months after starting treatment, then every
 * 3-12 months) but it is a *visit*, not a result, and scheduling visits is not what this protocol
 * does.
 */
export const asthmaOngoingMeasurements: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: ASTHMA_ONGOING_MEASUREMENTS_URL,
  version: ASTHMA_ONGOING_MEASUREMENTS_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: ASTHMA_ONGOING_MEASUREMENTS_URL }],
  name: 'asthma-ongoing-measurements',
  title: 'Asthma — ongoing measurements',
  extension: delivery('suggest'),
  status: 'active',
  experimental: true,
  type: clinicalProtocolType,
  date: '2026-09-15',
  publisher: 'Liza Health',
  description:
    'Results recommended for ongoing monitoring in asthma, expanded onto a care-plan topic when an asthma Condition is linked to it.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...ASTHMA_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [{ type: 'derived-from', display: GINA_2023.title, url: GINA_2023.url }],
  action: [
    {
      id: 'ongoing-measurements',
      title: 'Ongoing measurements',
      description: 'Results tracked on an ongoing basis for asthma.',
      action: [asthmaSpirometry, asthmaPeakFlow, asthmaOximetry],
    },
  ],
};
